/**
 * Pure route + image resolution for a global-search result row.
 *
 * The RPC (`global_search`, migration 20260703010200) returns a flat
 * `link_slug` per entity whose meaning is entity-specific. This module is the
 * single place that maps that slug to a real app route, so the overlay and the
 * `/search` page can never drift on the route split (Critical Rule #8):
 *   • dancer        → `/{handle}`              (short form, `dancerPath`)
 *   • organization  → `/{handle}`              (short form, `organizationPath`)
 *   • audition      → `/auditions/{slug}`      (or `/competitions/{slug}` when
 *                                               `is_competition`, mirroring the
 *                                               auditions→competitions redirect)
 *   • event         → `/events/{id}`
 *
 * Dancer/org links go through `dancerPath`/`organizationPath` (not raw
 * `/dancers/...` literals) because those long-form paths are legacy 301
 * redirects and `eslint-plugin-handles/no-hardcoded-handle-url` blocks the
 * literals. Audition/competition/event paths have no handle helper and are
 * built directly, matching the rest of the app.
 */
import { dancerPath, organizationPath } from '@kit/shared/handles';
import { mediaUrl, StorageBuckets } from '@kit/shared/storage';

import type { GlobalSearchResult } from './global-search-service';

type RouteFields = Pick<
  GlobalSearchResult,
  'kind' | 'linkSlug' | 'isCompetition'
>;

/**
 * Map a result row to its canonical in-app route, or `null` when the row has
 * no navigable target.
 *
 * A dancer/organization row links through its `handle` (`link_slug`), which is
 * NULLABLE: legacy / incomplete profiles (e.g. GDO competition registrants)
 * never got a handle, and super-admin global search (migration
 * `20260715140000_global_search_super_admin_sees_all_dancers_orgs`) surfaces
 * them. `dancerPath(null)` / `organizationPath(null)` produce the literal
 * string `/null`, whose page hard-404s ("Page not found") — the BAL-715
 * "profiles appear in search but 404 on click" report. Returning `null` here
 * lets the render sites show the row without a broken link. Audition `slug` and
 * event `id` are always present, so those branches never return null.
 */
export function resultHref(row: RouteFields): string | null {
  switch (row.kind) {
    case 'dancer':
      return row.linkSlug ? dancerPath(row.linkSlug) : null;
    case 'organization':
      return row.linkSlug ? organizationPath(row.linkSlug) : null;
    case 'audition':
      return row.isCompetition
        ? `/competitions/${row.linkSlug}`
        : `/auditions/${row.linkSlug}`;
    case 'event':
      return `/events/${row.linkSlug}`;
  }
}

type ImageFields = Pick<GlobalSearchResult, 'kind' | 'imageRef'>;

/**
 * Resolve the `<img>` src for a result row, or null when it has no image.
 *
 * The RPC's `image_ref` is NOT uniformly a URL — its meaning is per-kind, so
 * each kind is resolved through its own adapter (never rendered raw):
 *   • dancer       → a `dancer-media` bucket STORAGE PATH (e.g.
 *                    `{uuid}/headshot/x.jpg`). Resolved via `mediaUrl` to the
 *                    token-free `/api/media/cover` proxy — the same stable URL
 *                    the explore + dancers listings use (a raw path 404s, and
 *                    a baked signed token expires under SWR: 2026-06-12
 *                    all-initials incident). `mediaUrl` passes legacy Meteor
 *                    S3 full URLs through unchanged.
 *   • organization → a media-row UUID → `/api/media/{id}` proxy.
 *   • event        → `events.image_url`, already a full URL → as-is.
 *   • audition     → no image.
 */
export function resultImageSrc(row: ImageFields): string | null {
  if (!row.imageRef) return null;
  switch (row.kind) {
    case 'dancer':
      return mediaUrl(
        {
          bucket: StorageBuckets.DANCER_MEDIA,
          path: row.imageRef,
          visibility: 'public',
        },
        'THUMBNAIL',
      );
    case 'organization':
      return `/api/media/${row.imageRef}`;
    default:
      return row.imageRef;
  }
}
